import { useMutation, type UseMutationOptions } from '@tanstack/react-query'

import type { RetrofitName } from '@/shared/types/assessment.types'

import { measuresOverviewApi, type CreateCustomPackageResponse } from './measures-overview.api'

/**
 * Query key factory for measures overview queries
 */
export const measuresOverviewKeys = {
  all: ['measures-overview'] as const,
  customPackages: (buildingId: string) =>
    [...measuresOverviewKeys.all, 'custom-packages', buildingId] as const,
}

/**
 * Variables for creating a custom package
 */
export interface CreateCustomPackageVariables {
  buildingUuid: string
  retrofitNames: RetrofitName[]
}

/**
 * Hook to create a custom package for a specific building
 * @param options - Optional React Query mutation options
 */
export const useCreateCustomPackage = (
  options?: Omit<
    UseMutationOptions<CreateCustomPackageResponse, Error, CreateCustomPackageVariables>,
    'mutationFn'
  >
) => {
  return useMutation<CreateCustomPackageResponse, Error, CreateCustomPackageVariables>({
    mutationFn: ({ buildingUuid, retrofitNames }) =>
      measuresOverviewApi.createCustomPackage(buildingUuid, retrofitNames),
    ...options,
  })
}

/**
 * Variables for setting a custom package as main
 */
export interface SetCustomPackageAsMainVariables {
  customPackageUuid: string
}

/**
 * Hook to set a custom package as the main selected package
 * @param options - Optional React Query mutation options
 */
export const useSetCustomPackageAsMain = (
  options?: Omit<UseMutationOptions<void, Error, SetCustomPackageAsMainVariables>, 'mutationFn'>
) => {
  return useMutation<void, Error, SetCustomPackageAsMainVariables>({
    mutationFn: ({ customPackageUuid }) =>
      measuresOverviewApi.setCustomPackageAsMain(customPackageUuid),
    ...options,
  })
}

/**
 * Variables for deleting a custom package
 */
export interface DeleteCustomPackageVariables {
  customPackageUuid: string
}

/**
 * Hook to delete a custom package
 * @param options - Optional React Query mutation options
 */
export const useDeleteCustomPackage = (
  options?: Omit<UseMutationOptions<void, Error, DeleteCustomPackageVariables>, 'mutationFn'>
) => {
  return useMutation<void, Error, DeleteCustomPackageVariables>({
    mutationFn: ({ customPackageUuid }) =>
      measuresOverviewApi.deleteCustomPackage(customPackageUuid),
    ...options,
  })
}
